import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  afterNextRender,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { FormSaverComponent, InfoBannerComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter } from 'rxjs/operators';
import { viewLoader } from '../../../../../../assets/animations/animations';
import { nameRegex } from '../../../../../../assets/regex';
import { AcademicTitle } from '../../../../../enums/academic-title.enum';
import { Gender } from '../../../../../enums/gender.enum';
import {
  PlatformDocument,
  PlatformImportedCv,
  PlatformProfile,
} from '../../../../models/platform.models';
import { PlatformProfileService } from '../../../../services/platform-profile.service';
import { PlatformSectionSpyService } from '../../../../services/platform-section-spy.service';
import {
  fragmentFromUrl,
  observeScrollSections,
  scrollToUrlFragment,
} from '../../../../utils/platform-scroll.util';
import { pathFromUrl } from '../../../../../utils/platform-route.util';
import { PlatformProfileContactComponent } from '../../components/profile-contact/platform-profile-contact.component';
import { PlatformProfileDocumentsComponent } from '../../components/profile-documents/platform-profile-documents.component';
import { PlatformProfilePreferencesComponent } from '../../components/profile-preferences/platform-profile-preferences.component';
import { PlatformProfileLanguagesComponent } from '../../components/profile-languages/platform-profile-languages.component';
import { PlatformProfileSkillsComponent } from '../../components/profile-skills/platform-profile-skills.component';
import { PlatformProfileSocialComponent } from '../../components/profile-social/platform-profile-social.component';

@Component({
  selector: 'app-platform-profile-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    FormSaverComponent,
    InfoBannerComponent,
    PlatformProfileContactComponent,
    PlatformProfilePreferencesComponent,
    PlatformProfileSocialComponent,
    PlatformProfileDocumentsComponent,
    PlatformProfileSkillsComponent,
    PlatformProfileLanguagesComponent,
  ],
  templateUrl: './platform-profile-view.component.html',
  styleUrl: './platform-profile-view.component.scss',
  animations: [viewLoader],
})
export class PlatformProfileViewComponent implements OnInit, AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly profile = inject(PlatformProfileService);
  private readonly toastsService = inject(ToastsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sectionSpy = inject(PlatformSectionSpyService);

  readonly formSaver = viewChild(FormSaverComponent);
  private readonly scrollRoot = viewChild<ElementRef<HTMLElement>>('scrollRoot');
  readonly photoNonce = signal(0);
  readonly submitAttempt = signal(false);
  readonly saving = signal(false);

  readonly completenessPercent = this.profile.completenessPercent;
  readonly showCompletenessHint = this.profile.showProfileCompleteness;

  private readonly sectionIds = [
    'contact',
    'preferences',
    'social',
    'documents',
    'skills',
    'languages',
  ] as const;

  readonly profileForm: FormGroup = this.fb.group(
    {
      firstName: this.fb.control('', Validators.pattern(nameRegex)),
      lastName: this.fb.control('', Validators.pattern(nameRegex)),
      title: this.fb.control<Gender>(Gender.DIVERSE),
      academicTitle: this.fb.control<AcademicTitle | null>(AcademicTitle.NONE),
      currentOccupation: this.fb.control(''),
      email: this.fb.control({ value: '', disabled: true }),
      phone: this.fb.control('', Validators.pattern(/^[+0-9 ()\/.-]*$/)),
      city: this.fb.control(''),
      photoSrc: this.fb.control<string | null>(null),
      preferredLocations: this.fb.control<string[]>([]),
      workingModel: this.fb.control<string | null>(null),
      salaryMin: this.fb.control<number | null>(null),
      salaryMax: this.fb.control<number | null>(null),
      currency: this.fb.control('EUR'),
      availability: this.fb.control<string | null>(null),
      linkedin: this.fb.control('', this.socialUrlValidator('linkedin')),
      xing: this.fb.control('', this.socialUrlValidator('xing')),
      documents: this.fb.control<PlatformDocument[]>([]),
      pendingImportedCvs: this.fb.control<PlatformImportedCv[]>([]),
      skills: this.fb.control([]),
      languages: this.fb.control([]),
      documentParsingsRemaining: this.fb.control(5),
    },
    { validators: [this.salaryRangeValidator()] }
  );

  constructor() {
    effect(() => {
      const profile = this.profile.profile();
      untracked(() => this.syncPristineProfile(profile));
    });
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => scrollToUrlFragment(this.router.url));
    afterNextRender(() => this.bindSectionSpy());
  }

  ngOnInit(): void {
    this.applyProfile(this.profile.profile());
  }

  ngAfterViewInit(): void {
    this.applyProfile(this.profile.profile());
    this.formSaver()?.updateOriginalValue();
    scrollToUrlFragment(this.router.url);
  }

  /**
   * Persists the profile and resets the save bar baseline.
   */
  onSave(): void {
    this.submitAttempt.set(true);
    this.profileForm.markAllAsTouched();
    if (this.profileForm.invalid) {
      this.formSaver()?.shake();
      this.scrollToFirstInvalid();
      return;
    }
    this.saving.set(true);
    // Pass pending import decisions through untouched — save() adopts them via API.
    const profile = this.formToProfile();
    this.profile.save(profile).subscribe({
      next: () => {
        this.applyProfile(this.profile.profile());
        this.profileForm.markAsPristine();
        this.submitAttempt.set(false);
        this.saving.set(false);
        this.formSaver()?.updateOriginalValue();
        this.toastsService.success('jobboard.platform.profile.saved');
      },
      error: () => {
        this.saving.set(false);
        this.formSaver()?.shake();
        this.toastsService.warning('jobboard.platform.profile.save-error');
      },
    });
  }

  /**
   * Recreates the photo editor after discarding unsaved changes.
   */
  onDiscard(): void {
    this.submitAttempt.set(false);
    this.photoNonce.update((nonce) => nonce + 1);
  }

  /**
   * Keeps the save-bar baseline in sync after document API persists.
   */
  onDocumentsSynced(): void {
    this.formSaver()?.patchOriginalValue({
      documents: this.profileForm.controls['documents'].value,
      documentParsingsRemaining:
        this.profileForm.controls['documentParsingsRemaining'].value,
    });
  }

  /**
   * Shows a toast when a duplicate skill is added.
   */
  onDuplicateSkill(): void {
    this.toastsService.warning('jobboard.platform.profile.duplicate-skill');
  }

  /**
   * Shows a toast when a duplicate language is added.
   */
  onDuplicateLanguage(): void {
    this.toastsService.warning('jobboard.platform.profile.duplicate-language');
  }

  /**
   * Applies profile data while the form has no user edits.
   * @param profile - Latest profile from the session
   */
  private syncPristineProfile(profile: PlatformProfile): void {
    if (!this.profileForm.pristine) {
      return;
    }
    this.applyProfile(profile);
    this.formSaver()?.updateOriginalValue();
  }

  /**
   * Tracks the visible profile section for the sidenav and URL hash.
   */
  private bindSectionSpy(): void {
    const container = this.scrollRoot()?.nativeElement;
    if (!container) {
      return;
    }
    const path = pathFromUrl(this.router.url);
    const fragment = fragmentFromUrl(this.router.url);
    if (fragment) {
      this.sectionSpy.setSection(path, fragment, { syncUrl: false });
    }
    const stop = observeScrollSections(
      container,
      this.sectionIds,
      (sectionId) => {
        this.sectionSpy.setSection(path, sectionId);
      },
      { emitInitial: false }
    );
    this.destroyRef.onDestroy(stop);
  }

  /**
   * Maps the current profile onto the form.
   * @param profile - Profile to apply
   */
  private applyProfile(profile: PlatformProfile): void {
    this.profileForm.patchValue({
      firstName: profile.firstName,
      lastName: profile.lastName,
      title: profile.title,
      academicTitle: profile.academicTitle ?? AcademicTitle.NONE,
      currentOccupation: profile.currentOccupation,
      email: profile.email,
      phone: profile.phone,
      city: profile.city,
      photoSrc: profile.photoSrc,
      preferredLocations: [...profile.preferredLocations],
      workingModel: profile.workingModel,
      salaryMin: profile.salaryMin,
      salaryMax: profile.salaryMax,
      currency: profile.currency,
      availability: profile.availability,
      linkedin: profile.linkedin,
      xing: profile.xing,
      documents: profile.documents.map((document) => ({ ...document })),
      pendingImportedCvs: profile.pendingImportedCvs.map((cv) => ({ ...cv })),
      skills: profile.skills.map((skill) => ({ ...skill })),
      languages: profile.languages.map((language) => ({ ...language })),
      documentParsingsRemaining: profile.documentParsingsRemaining,
    });
  }

  /**
   * Reads the profile from the form value.
   * @returns Profile snapshot
   */
  private formToProfile(): PlatformProfile {
    const value = this.profileForm.getRawValue();
    return {
      firstName: value.firstName ?? '',
      lastName: value.lastName ?? '',
      title: value.title ?? Gender.DIVERSE,
      academicTitle: value.academicTitle ?? AcademicTitle.NONE,
      currentOccupation: value.currentOccupation ?? '',
      email: value.email ?? '',
      phone: value.phone ?? '',
      city: value.city ?? '',
      photoSrc: value.photoSrc ?? null,
      preferredLocations: [...(value.preferredLocations ?? [])],
      workingModel: value.workingModel ?? null,
      salaryMin: value.salaryMin ?? null,
      salaryMax: value.salaryMax ?? null,
      currency: value.currency ?? 'EUR',
      availability: value.availability ?? null,
      linkedin: value.linkedin ?? '',
      xing: value.xing ?? '',
      documents: [...(value.documents ?? [])],
      pendingImportedCvs: [...(value.pendingImportedCvs ?? [])],
      skills: [...(value.skills ?? [])],
      languages: [...(value.languages ?? [])],
      documentParsingsRemaining: value.documentParsingsRemaining ?? 5,
      startDate: this.profile.profile().startDate,
      isaCompleted: this.profile.profile().isaCompleted,
    };
  }

  /**
   * Scrolls the first invalid field into view.
   */
  private scrollToFirstInvalid(): void {
    setTimeout(() => {
      document
        .querySelector<HTMLElement>(
          '.profile-view .form-group.invalid, .profile-view .validation-text'
        )
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  /**
   * Validates LinkedIn / Xing profile URLs (allows common host variants).
   * @param kind - Social network
   * @returns Validator
   */
  private socialUrlValidator(kind: 'linkedin' | 'xing'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = String(control?.value ?? '').trim();
      if (!raw) {
        return null;
      }
      try {
        const url = new URL(raw);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          return { start: { value: control.value } };
        }
        const host = url.hostname.toLowerCase();
        const path = url.pathname.toLowerCase();
        const valid =
          kind === 'linkedin'
            ? /(^|\.)linkedin\.com$/.test(host) && path.startsWith('/in/')
            : /(^|\.)xing\.com$/.test(host) && path.startsWith('/profile/');
        return valid ? null : { start: { value: control.value } };
      } catch {
        return { start: { value: control.value } };
      }
    };
  }

  /**
   * Validates that salary min is not greater than salary max.
   * @returns Group validator
   */
  private salaryRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const min = control.get('salaryMin')?.value;
      const max = control.get('salaryMax')?.value;
      if (min == null || max == null || min === '' || max === '') {
        return null;
      }
      return Number(min) > Number(max) ? { salaryRange: true } : null;
    };
  }
}
